/*
 * AuthMetadataRestorer
 * Restores authMetadata from Auth0 user object on page refresh
 * This component must be inside Auth0Provider
 */
import { AuthMetadata, getUserPermissions } from '@repo/frontend-types';
import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserStore } from '@repo/front-end-state';
import { extractClaims } from '@repo/front-end-utils';
import Config from '../../common/Config';

interface Props {
  children: React.ReactNode;
}

export const AuthMetadataRestorer: React.FC<Props> = ({ children }) => {
  const { user, isAuthenticated, isLoading: auth0IsLoading, getAccessTokenSilently } = useAuth0();
  const { authMetadata, setAuthMetadata } = useUserStore();

  // Restore authMetadata from Auth0 user object on mount/refresh if missing
  useEffect(() => {
    // Wait for Auth0 to finish loading
    if (auth0IsLoading) {
      return;
    }

    // If authenticated and have user, restore tokens and basic info (preserve orgBID if it exists)
    if (isAuthenticated && user) {
      const restoreMetadata = async () => {
        try {
          const claims = extractClaims(user, Config.auth0Namespace);
          if (claims && claims.roles && claims.roles.length > 0) {
            // Check if we need to set tokens (they might be missing even if authMetadata exists)
            const needsTokens =
              !authMetadata?.tokens?.mapboxToken || !authMetadata?.tokens?.modelUrlKey;
            const needsAccessToken = !authMetadata?.tokens?.accessToken;
            const needsBasicInfo = !authMetadata?.roles || !authMetadata?.name;
            // Only restore legal from user object if it's missing from authMetadata
            // This prevents overwriting legal data that was just updated after accepting terms
            const needsLegal = !authMetadata?.legal && claims.legal !== undefined;

            if (needsTokens || needsBasicInfo || needsAccessToken || needsLegal) {
              // Build update object with only the missing parts to avoid unnecessary re-renders
              const update: Partial<AuthMetadata> = {};

              if (needsBasicInfo) {
                update.roles = claims.roles;
                update.permissions = getUserPermissions(claims.roles);
                update.name = user.name;
                update.firstName = user.given_name;
                update.lastName = user.family_name;
                update.email = user.email;
              }

              if (needsLegal && claims.legal) {
                update.legal = claims.legal;
              }

              if (needsTokens || needsAccessToken) {
                // Fetch accessToken if needed
                let accessToken = authMetadata?.tokens?.accessToken;
                if (needsAccessToken) {
                  try {
                    accessToken = await getAccessTokenSilently();
                  } catch (error) {
                    console.error('Failed to get access token during restoration:', error);
                  }
                }

                update.tokens = {
                  ...authMetadata?.tokens,
                  mapboxToken: needsTokens ? claims.mapboxToken : authMetadata?.tokens?.mapboxToken,
                  modelUrlKey: needsTokens ? claims.modelUrlKey : authMetadata?.tokens?.modelUrlKey,
                  ...(accessToken && { accessToken }),
                };
              }

              // Don't overwrite orgBID if it already exists (from localStorage)
              // orgBID will be preserved by the merge in setAuthMetadata
              setAuthMetadata(update);
            }
          }
        } catch (error) {
          console.error('Error restoring authMetadata from user:', error);
        }
      };

      restoreMetadata();
    }
  }, [
    isAuthenticated,
    user,
    auth0IsLoading,
    authMetadata,
    setAuthMetadata,
    getAccessTokenSilently,
  ]);

  return <>{children}</>;
};
